<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import { ChevronDown, ExternalLink, LogOut, User } from 'lucide-vue-next';

import { useAuthStore } from '@/stores/auth.js';

interface NavLink {
  to: { name: string };
  label: string;
}

const { t } = useI18n();
const authStore = useAuthStore();

const navLinks = computed<NavLink[]>(() => {
  const links: NavLink[] = [{ to: { name: 'home' }, label: 'Кабинет' }];
  if (authStore.isAdmin) {
    // Заявками админ управляет из проектов — отдельной вкладки «Заявки» нет.
    links.push({ to: { name: 'admin-projects' }, label: 'Проекты' });
  }
  if (authStore.isEnterpriseCoordinator) {
    links.push({ to: { name: 'my-applications' }, label: 'Мои заявки' });
  }
  if (authStore.isRegionCoordinator) {
    links.push({ to: { name: 'region' }, label: 'Мониторинг региона' });
  }
  return links;
});

const lkUrl = import.meta.env.VITE_LK_URL ?? 'http://localhost:5173';

const userInitials = computed(() => {
  const name = authStore.user?.displayName ?? authStore.user?.email ?? '';
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return (name[0] ?? '?').toUpperCase();
});

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}
function closeMenu() {
  menuOpen.value = false;
}
function handleLogout() {
  closeMenu();
  void authStore.logout();
}

function handleDocumentClick(event: MouseEvent) {
  if (!menuOpen.value) return;
  const target = event.target as Node | null;
  if (menuRef.value && target && !menuRef.value.contains(target)) {
    closeMenu();
  }
}
function handleKey(event: KeyboardEvent) {
  if (event.key === 'Escape') closeMenu();
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleKey);
});
</script>

<template>
  <div class="shell">
    <header class="shell__topbar">
      <RouterLink :to="{ name: 'home' }" class="shell__brand" :aria-label="t('app.name')">
        <svg class="shell__logo" viewBox="0 0 32 32" aria-hidden="true">
          <defs>
            <linearGradient id="pt-shell-logo" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stop-color="#F2814F" />
              <stop offset="100%" stop-color="#E4491F" />
            </linearGradient>
          </defs>
          <rect width="32" height="32" rx="8" fill="url(#pt-shell-logo)" />
          <path
            d="M8 22c4 0 4-8 8-8s4 6 8 6"
            stroke="#FFFFFF"
            stroke-width="2"
            stroke-linecap="round"
            stroke-dasharray="0.5 4"
            fill="none"
          />
          <circle cx="8" cy="22" r="2.4" fill="#FFFFFF" />
        </svg>
        <span class="shell__brand-name">{{ t('app.name') }}</span>
      </RouterLink>

      <nav v-if="navLinks.length" class="shell__nav">
        <RouterLink
          v-for="link in navLinks"
          :key="link.to.name"
          :to="link.to"
          class="shell__nav-link"
          active-class="shell__nav-link--active"
        >
          {{ link.label }}
        </RouterLink>
      </nav>

      <div class="shell__actions">
        <a class="shell__lk-link" :href="lkUrl">
          <ExternalLink :size="14" :stroke-width="1.75" />
          <span>{{ t('shell.toLk') }}</span>
        </a>

        <div ref="menuRef" class="shell__user-menu">
          <button
            type="button"
            class="shell__user-trigger"
            :aria-expanded="menuOpen"
            aria-haspopup="menu"
            @click="toggleMenu"
          >
            <span class="shell__avatar">{{ userInitials }}</span>
            <span class="shell__user-meta">
              <span class="shell__user-name">{{ authStore.user?.displayName ?? '—' }}</span>
              <span class="shell__user-email">{{ authStore.user?.email ?? '' }}</span>
            </span>
            <ChevronDown :size="16" :stroke-width="1.75" class="shell__caret" />
          </button>

          <div v-if="menuOpen" class="shell__dropdown" role="menu">
            <RouterLink
              :to="{ name: 'home' }"
              class="shell__dropdown-item"
              role="menuitem"
              @click="closeMenu"
            >
              <User :size="16" :stroke-width="1.75" />
              <span>{{ t('shell.profile') }}</span>
            </RouterLink>
            <div class="shell__dropdown-sep" role="separator" />
            <button
              type="button"
              class="shell__dropdown-item shell__dropdown-item--danger"
              role="menuitem"
              @click="handleLogout"
            >
              <LogOut :size="16" :stroke-width="1.75" />
              <span>{{ t('shell.logout') }}</span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <main class="shell__content">
      <slot />
    </main>
  </div>
</template>

<style scoped>
.shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--color-bg-surface);
}

.shell__topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  height: 56px;
  padding: 0 var(--space-5);
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border-subtle);
}

.shell__brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-text);
  text-decoration: none;
}
.shell__brand:hover { text-decoration: none; }

.shell__logo {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.shell__brand-name {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  letter-spacing: var(--letter-spacing-tight);
}

.shell__nav {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0 auto 0 var(--space-6);
}

.shell__nav-link {
  display: inline-flex;
  align-items: center;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: background var(--transition-fast), color var(--transition-fast);
}
.shell__nav-link:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}
.shell__nav-link--active {
  background: var(--color-bg-soft);
  color: var(--color-text);
}

.shell__actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-left: auto;
}

.shell__lk-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: background var(--transition-fast), color var(--transition-fast);
}
.shell__lk-link:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}

.shell__user-menu { position: relative; }

.shell__user-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  padding: 4px 8px 4px 4px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: var(--radius-full);
  cursor: pointer;
  color: var(--color-text);
  transition: background var(--transition-fast), border-color var(--transition-fast);
}
.shell__user-trigger:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-subtle);
}
.shell__user-trigger[aria-expanded='true'] {
  background: var(--color-bg-soft);
  border-color: var(--color-border);
}

.shell__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  background: #fce8df;
  color: #d6431a;
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
}

.shell__user-meta {
  display: none;
  flex-direction: column;
  text-align: left;
  max-width: 180px;
}
.shell__user-name {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shell__user-email {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shell__caret { display: none; color: var(--color-text-muted); }

@media (min-width: 720px) {
  .shell__user-meta,
  .shell__caret { display: inline-flex; }
}

.shell__dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: var(--space-1);
  z-index: var(--z-dropdown);
}

.shell__dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  font-family: inherit;
  font-size: var(--font-size-base);
  color: var(--color-text);
  text-decoration: none;
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}
.shell__dropdown-item:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}
.shell__dropdown-item--danger { color: var(--color-danger); }
.shell__dropdown-item--danger:hover {
  background: var(--color-danger-soft);
  color: var(--color-danger-hover);
}

.shell__dropdown-sep {
  height: 1px;
  margin: var(--space-1) var(--space-1);
  background: var(--color-border-subtle);
}

.shell__content {
  flex: 1;
  padding: var(--space-8) var(--space-6);
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  min-width: 0;
}
</style>
